import { extractUtms } from '@/lib/attribution/landingUtms'

/**
 * Sentry performance tracing for the acquisition journey only: product pages
 * and checkout. Every other route stays untraced so the span quota is spent
 * where load time costs sales.
 *
 * Transaction names are the parameterized pages-router route (`/[locale]/daily30`),
 * for page loads, navigations and the INP spans that inherit them.
 */
const PDP_ROUTES = new Set([
  '/[locale]/daily30',
  '/[locale]/daily30-pmax',
  '/[locale]/daily30/plans',
  '/[locale]/buyguthealthbar',
  '/[locale]/buyguthealthtest',
  '/[locale]/buymembership',
  '/[locale]/buymembership/plans',
])

export const CHECKOUT_ROUTES = new Set([
  '/[locale]/daily30/checkout',
  '/[locale]/buyguthealthbar/checkout',
  '/[locale]/buyguthealthtest/checkout',
  '/[locale]/buymembership/checkout',
])

// The App plans page, where step 5a (plans ready) is timed — see lib/sentry/journeyMeasurements.ts
export const PLANS_ROUTES = new Set(['/[locale]/buymembership/plans'])

// Sign-up interstitials a signed-out shopper passes on the way to checkout — see lib/sentry/signUpTiming.ts
export const SIGN_UP_ROUTES = new Set(
  ['daily30', 'buyguthealthbar', 'buymembership'].flatMap((product) =>
    ['signup', 'email', 'login'].map((page) => `/[locale]/${product}/${page}`),
  ),
)

// The purchase recorded on the page Stripe returns to, which can be any route — see lib/sentry/paymentTiming.ts
export const PURCHASE_TRANSACTION_NAME = 'checkout.purchase'

// Checkout is ~10% of the PDP traffic and the page we're investigating, so it
// is traced in full; PDPs are sampled to keep spans well inside the plan quota.
export const CHECKOUT_SAMPLE_RATE = 1
export const PDP_SAMPLE_RATE = 0.25

interface SamplingContextLike {
  name?: string
  transactionContext?: { name: string }
}

/**
 * `tracesSampler` for the browser SDK. Sentry v7 passes `name` at runtime but
 * only types `transactionContext`; v10 drops `transactionContext`. Reading both
 * keeps this working across the SDK upgrade.
 */
export const tracesSampler = ({ name, transactionContext }: SamplingContextLike): number => {
  const route = name ?? transactionContext?.name ?? ''
  if (CHECKOUT_ROUTES.has(route) || SIGN_UP_ROUTES.has(route) || route === PURCHASE_TRANSACTION_NAME) {
    return CHECKOUT_SAMPLE_RATE
  }
  if (PDP_ROUTES.has(route)) return PDP_SAMPLE_RATE
  return 0
}

interface TransactionEventLike {
  spans?: Array<{ op?: string }>
}

// GTM and Optimizely call performance.mark/measure ~180 times per page load,
// which would multiply span usage ~5x without telling us anything about load
// time. v8+ has `ignorePerformanceApiSpans`; on v7 we drop them here.
// Long tasks are off in BrowserTracing, but older SDK paths still emit them.
const LOW_VALUE_SPAN_OPS = new Set(['mark', 'measure', 'ui.long-task'])

// One span per script, image and stylesheet was ~60% of the site's span usage,
// and nobody reads them: load time is judged from web vitals and the journey steps.
const isResourceSpan = (op: string) => op.startsWith('resource.')

/**
 * `beforeSendTransaction`: strips spans that cost quota without explaining load time.
 * Dropped here they never leave the browser, so they aren't billed.
 */
export const dropLowValueSpans = <T extends TransactionEventLike>(event: T): T => {
  if (event.spans) {
    event.spans = event.spans.filter((span) => {
      const op = span.op ?? ''
      return !LOW_VALUE_SPAN_OPS.has(op) && !isResourceSpan(op)
    })
  }
  return event
}

/**
 * Whether a transaction's duration is a real wait. Only `ok` belongs in latency
 * charts; the rest are kept so visit counts stay complete.
 *
 * - `backgrounded`: the tab was hidden mid-load and the SDK ended it early (`cancelled`)
 * - `timed_out`: it hit the SDK's 30s final timeout (`deadline_exceeded`)
 * - `prerendered`: the page was loaded before the visitor opened it, so the
 *   duration starts before they saw anything
 * - `hidden_at_start`: same, for in-app browsers that preload links while hidden
 * - `too_long`: a page load or navigation longer than the final timeout can produce, so the start
 *   time is stale
 */
export type TimingQuality = 'ok' | 'backgrounded' | 'timed_out' | 'prerendered' | 'hidden_at_start' | 'too_long'

// The v7 SDK ends idle transactions after 30s; anything longer started long before the visit.
const MAX_PLAUSIBLE_DURATION_S = 30

/** Read once at startup, before the pageload transaction can be affected by later visibility changes. */
export const getPageLoadTimingIssue = (): TimingQuality | undefined => {
  const navigation = performance.getEntriesByType('navigation')[0] as
    | (PerformanceNavigationTiming & { activationStart?: number })
    | undefined
  if ((navigation?.activationStart ?? 0) > 0) return 'prerendered'
  if (document.visibilityState === 'hidden') return 'hidden_at_start'
  return undefined
}

interface AnnotatableTransactionEvent {
  start_timestamp?: number
  timestamp?: number
  tags?: Record<string, unknown>
  contexts?: { trace?: { op?: string; status?: string } }
  spans?: Array<{ data?: Record<string, unknown> }>
}

export const getTimingQuality = (
  event: AnnotatableTransactionEvent,
  pageLoadIssue: TimingQuality | undefined,
): TimingQuality => {
  const trace = event.contexts?.trace
  if (trace?.status === 'cancelled') return 'backgrounded'
  if (trace?.status === 'deadline_exceeded') return 'timed_out'
  if (trace?.op === 'pageload' && pageLoadIssue) return pageLoadIssue
  // Only the SDK's idle transactions can outlive their start; the step transactions we start
  // ourselves (sign up, pay, purchase) begin with their step, so a long one is a slow shopper.
  if (trace?.op !== 'pageload' && trace?.op !== 'navigation') return 'ok'
  const duration = (event.timestamp ?? 0) - (event.start_timestamp ?? 0)
  if (duration > MAX_PLAUSIBLE_DURATION_S) return 'too_long'
  return 'ok'
}

/**
 * Tags the transaction with its timing quality and copies that plus the
 * segmentation tags onto every child span. Scope tags only reach the
 * transaction itself, so without this the API calls inside a checkout can't be
 * split by in-app browser.
 */
export const annotateTransaction = <T extends AnnotatableTransactionEvent>(
  event: T,
  tracingTags: Record<string, string>,
  pageLoadIssue: TimingQuality | undefined,
): T => {
  const timingQuality = getTimingQuality(event, pageLoadIssue)
  event.tags = { ...event.tags, timing_quality: timingQuality }
  const spanAttributes = { ...tracingTags, timing_quality: timingQuality }
  for (const span of event.spans ?? []) span.data = { ...span.data, ...spanAttributes }
  return event
}

type EnvelopeLike = [object, Array<[{ type?: string }, unknown]>]

interface StandaloneSpanLike {
  setAttribute: (key: string, value: string) => void
  spanContext: () => { traceId: string }
}

const isStandaloneSpan = (item: unknown): item is StandaloneSpanLike =>
  !!item &&
  typeof (item as { setAttribute?: unknown }).setAttribute === 'function' &&
  typeof (item as { spanContext?: unknown }).spanContext === 'function'

/** The client-level part of a dynamic sampling context; `trace_id` comes from the span. */
export interface SamplingContextBase {
  public_key: string
  environment: string
  release?: string
}

/**
 * Sentry v7 builds INP spans with a fixed attribute list and sends them straight
 * to the transport, skipping scope tags and every `beforeSend*` hook. Stamping
 * the segmentation onto standalone spans here is the only way they can be split
 * by in-app browser and traffic source.
 *
 * v7 also sends those envelopes without a `trace` header, which Sentry's ingest
 * now rejects as `missing_dsc`, so we add the sampling context it would have built.
 */
export const enrichStandaloneSpans = <T extends EnvelopeLike>(
  envelope: T,
  attributes: Record<string, string>,
  samplingContext: SamplingContextBase | undefined,
): T => {
  const envelopeHeader = envelope[0] as { trace?: unknown }
  for (const [header, item] of envelope[1]) {
    if (header.type !== 'span' || !isStandaloneSpan(item)) continue
    for (const [key, value] of Object.entries(attributes)) item.setAttribute(key, value)
    if (!envelopeHeader.trace && samplingContext) {
      envelopeHeader.trace = { ...samplingContext, trace_id: item.spanContext().traceId, sampled: 'true' }
    }
  }
  return envelope
}

// `browser`: any other browser (Safari, Chrome, desktop), so dashboard legends read as a browser, not a missing value
export type InAppBrowser = 'facebook' | 'instagram' | 'tiktok' | 'browser'

/**
 * Social apps' in-app browsers identify themselves only in the user agent. GA4
 * cuts the user agent at 100 characters, which drops these tokens, so we
 * classify it here instead.
 */
export const detectInAppBrowser = (userAgent: string): InAppBrowser => {
  if (/Instagram/.test(userAgent)) return 'instagram'
  if (/FBAN|FBAV|FB_IAB|FBIOS/.test(userAgent)) return 'facebook'
  // TikTok's webview carries its original `musical_ly` app id and ByteDance's engine name
  if (/TikTok|musical_ly|BytedanceWebview/.test(userAgent)) return 'tiktok'
  return 'browser'
}

/**
 * Tags set once per document, so they ride on every transaction and error.
 * `utm_source` is the landing value, matching first-touch attribution; it is
 * `none` when the visit landed without one. No click-id value is sent.
 */
export const getTracingTags = (userAgent: string, search: string): Record<string, string> => {
  const utms = extractUtms(new URLSearchParams(search))
  return {
    in_app_browser: detectInAppBrowser(userAgent),
    utm_source: utms.utm_source?.toLowerCase() ?? 'none',
    has_fbclid: utms.fbclid ? 'true' : 'false',
  }
}
